import { getSunPosition, toUtcDate } from "./sun"
import { computeShadowPolygon } from "./shadows"

// Sums direct-sun minutes per grid cell across a whole day.
//
// Complexity: rows * cols * (24 * 60 / stepMinutes) * objects. For a typical
// plot (50×40 m at 0.5 m resolution, 15-min steps, 10 objects) that's
// ~4M point-in-polygon checks, which is ~300-500 ms on a modern laptop.
// Fine for a one-shot on-demand compute. If it becomes a bottleneck we can
// move to a Web Worker without changing the call site.
export function computeHeatmap({
  plot,
  objects,
  lat,
  lng,
  dateIso,
  utcOffsetHours = 0,
  resolutionM = 0.5,
  stepMinutes = 15,
}) {
  const bb = polygonBoundingBox(plot.polygonM)
  const cols = Math.max(1, Math.ceil(bb.widthM / resolutionM))
  const rows = Math.max(1, Math.ceil(bb.heightM / resolutionM))
  const sunMinutes = new Float32Array(cols * rows)
  // Time-of-day bands (Stage 9c advisor): per-cell direct-sun minutes split into
  // morning / midday / afternoon by local wall-clock hour. Additive to the
  // result — existing callers ignore `bandMinutes`.
  const bandMorning = new Float32Array(cols * rows)
  const bandMidday = new Float32Array(cols * rows)
  const bandAfternoon = new Float32Array(cols * rows)

  // Inside-plot mask: static for a given plot, pre-compute once.
  const inPlot = new Uint8Array(cols * rows)
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const xM = bb.minX + (c + 0.5) * resolutionM
      const yM = bb.minY + (r + 0.5) * resolutionM
      inPlot[r * cols + c] = pointInPolygon(xM, yM, plot.polygonM) ? 1 : 0
    }
  }

  const day = parseDayStart(dateIso)

  let daylightMinutes = 0
  for (let m = 0; m < 24 * 60; m += stepMinutes) {
    const iso = isoAt(day, m)
    const utc = toUtcDate(iso, utcOffsetHours)
    const sun = getSunPosition(utc, lat, lng)
    if (sun.altitude <= 0) continue

    daylightMinutes += stepMinutes

    // Which band this step falls in (morning <11:00, midday 11–15, afternoon ≥15).
    const band = m < 11 * 60 ? bandMorning : m < 15 * 60 ? bandMidday : bandAfternoon

    // Precompute shadow polygons and their bounding boxes for this sun step.
    const shadows = []
    for (const obj of objects) {
      const poly = computeShadowPolygon(obj, sun.azimuth, sun.altitude)
      if (!poly || poly.length < 3) continue
      shadows.push({ poly, bb: polygonBoundingBox(poly) })
    }

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const idx = r * cols + c
        if (!inPlot[idx]) continue

        const xM = bb.minX + (c + 0.5) * resolutionM
        const yM = bb.minY + (r + 0.5) * resolutionM

        let shaded = false
        for (const s of shadows) {
          if (xM < s.bb.minX || xM > s.bb.maxX || yM < s.bb.minY || yM > s.bb.maxY) continue
          if (pointInPolygon(xM, yM, s.poly)) { shaded = true; break }
        }
        if (!shaded) {
          sunMinutes[idx] += stepMinutes
          band[idx] += stepMinutes
        }
      }
    }
  }

  return {
    sunMinutes, cols, rows, resolutionM, bb, daylightMinutes, inPlot,
    bandMinutes: { morning: bandMorning, midday: bandMidday, afternoon: bandAfternoon },
  }
}

// Per-room direct-sun exposure (Stage 9b). Averages the sun-minutes of the
// heatmap cells whose centres fall inside `polygonPlot` (the room footprint in
// the PLOT frame) and converts to hours.
//
// IMPORTANT: the heatmap passed here should be computed with the room's own
// house EXCLUDED from `objects` — otherwise every interior cell sits under the
// house's own shadow and reads ~0. So this measures the room area's solar
// exposure by position (orientation/neighbour shadows/latitude/season); window
// placement refines it in Stage 9c.
//
// Returns { sunHours, daylightHours, ratio, cells, level } where level is
// "low" | "medium" | "high" (or "none" when no cell centre lands in the room —
// e.g. a room smaller than one grid cell).
export function computeRoomInsolation(result, polygonPlot) {
  const { sunMinutes, cols, rows, resolutionM, bb, daylightMinutes } = result
  let total = 0
  let count = 0
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const xM = bb.minX + (c + 0.5) * resolutionM
      const yM = bb.minY + (r + 0.5) * resolutionM
      if (!pointInPolygon(xM, yM, polygonPlot)) continue
      total += sunMinutes[r * cols + c]
      count++
    }
  }
  if (count === 0) return { sunHours: 0, daylightHours: daylightMinutes / 60, ratio: 0, cells: 0, level: "none" }
  const avgMinutes = total / count
  const sunHours = avgMinutes / 60
  const ratio = daylightMinutes > 0 ? avgMinutes / daylightMinutes : 0
  return { sunHours, daylightHours: daylightMinutes / 60, ratio, cells: count, level: insolationLevel(sunHours) }
}

// Three-band diagnosis. Thresholds are deliberate, documented defaults for
// residential use (a foundation for the Stage 9c room advisor): under 2 h of
// direct sun is "low" (poor for a living space), 2–5 h "medium", 5 h+ "high".
export function insolationLevel(sunHours) {
  if (sunHours < 2) return "low"
  if (sunHours < 5) return "medium"
  return "high"
}

// Ray-cast point-in-polygon. Polygon is [[x, y], ...] — treats first vertex
// as implicitly reconnected to last so unclosed polygons work.
export function pointInPolygon(x, y, polygon) {
  let inside = false
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [xi, yi] = polygon[i]
    const [xj, yj] = polygon[j]
    const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi
    if (intersect) inside = !inside
  }
  return inside
}

// Duplicate of projection.js#polygonBoundingBox kept local to keep this file
// import-free from browser-only modules (heatmap could later move to a Worker).
export function polygonBoundingBox(polygonM) {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
  for (const [x, y] of polygonM) {
    if (x < minX) minX = x
    if (x > maxX) maxX = x
    if (y < minY) minY = y
    if (y > maxY) maxY = y
  }
  return { minX, minY, maxX, maxY, widthM: maxX - minX, heightM: maxY - minY }
}

function parseDayStart(iso) {
  const d = new Date(iso)
  d.setHours(0, 0, 0, 0)
  return d
}

function isoAt(day, minuteOfDay) {
  const d = new Date(day)
  d.setHours(Math.floor(minuteOfDay / 60), minuteOfDay % 60, 0, 0)
  const pad = (n) => String(n).padStart(2, "0")
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:00`
}

// Blue → yellow gradient keyed on ratio (0..1).
// Uses inPlot mask so outside-plot cells become fully transparent (critical
// for L-shaped / irregular plots, where the bounding box spills past the
// polygon and would otherwise paint a tinted rectangle over non-plot area).
export function heatmapToImageData(result) {
  const { sunMinutes, cols, rows, daylightMinutes, inPlot } = result
  const denom = daylightMinutes > 0 ? daylightMinutes : 1
  const data = new Uint8ClampedArray(cols * rows * 4)
  for (let i = 0; i < sunMinutes.length; i++) {
    if (inPlot && !inPlot[i]) {
      // outside plot → fully transparent
      data[i * 4 + 3] = 0
      continue
    }
    const ratio = Math.min(1, sunMinutes[i] / denom)
    const { r, g, b } = gradient(ratio)
    data[i * 4 + 0] = r
    data[i * 4 + 1] = g
    data[i * 4 + 2] = b
    // Shaded in-plot cells need enough opacity to read as "blue" rather
    // than a pale wash. 140 strikes a balance between "clear data" and
    // "buildings remain visible underneath".
    data[i * 4 + 3] = sunMinutes[i] > 0 ? 200 : 140
  }
  return { data, width: cols, height: rows }
}

// Lerps through four stops: deep blue → teal → yellow → white-ish, mimicking
// a "cool to hot" sun-hours map without requiring a dependency.
function gradient(t) {
  const stops = [
    [0.00, 30, 58, 138],   // indigo-900
    [0.33, 14, 116, 144],  // teal-700
    [0.66, 234, 179, 8],   // yellow-500
    [1.00, 254, 240, 138], // yellow-200
  ]
  for (let i = 0; i < stops.length - 1; i++) {
    const [t0, r0, g0, b0] = stops[i]
    const [t1, r1, g1, b1] = stops[i + 1]
    if (t <= t1) {
      const k = (t - t0) / (t1 - t0)
      return { r: lerp(r0, r1, k), g: lerp(g0, g1, k), b: lerp(b0, b1, k) }
    }
  }
  const last = stops[stops.length - 1]
  return { r: last[1], g: last[2], b: last[3] }
}

const lerp = (a, b, k) => Math.round(a + (b - a) * k)
